import React, { useEffect, useState, useCallback } from "react";
import { getPatientBookings } from "../Services/api";
import { RefreshCcw, CalendarCheck, Clock, User, Hash } from "lucide-react";
import { useTranslation } from "react-i18next";
import Loader from './Loader';
import { Video } from "lucide-react";

export default function BookingsTab({ patientId }) {
  const [bookings, setBookings] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("");
  console.log("COMING in bookings");

  const { t } = useTranslation();

  const _storedPatient = (() => {
    try {
      const raw = localStorage.getItem("patient");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.warn("Could not parse patient from localStorage", e);
      return null;
    }
  })();

  const patientName =
    [
      _storedPatient?.salutation,  // 👈 add this
      _storedPatient?.name ||
      [_storedPatient?.firstName, _storedPatient?.middleName, _storedPatient?.lastName]
        .filter(Boolean)
        .join(" "),
    ]
      .filter(Boolean)
      .join(" ") || "";

  const patientIdentifier = _storedPatient?.patientId || _storedPatient?.id || "";

  const fetchBookings = useCallback(async () => {
    setLoadingMessage(t("bookings.loading", "Fetching your bookings..."));
    setLoading(true);
    try {
      const all = await getPatientBookings(patientId);
      setBookings(all);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setBookings({ upcoming: [], past: [] });
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  useEffect(() => {
    if (patientId) fetchBookings();
  }, [patientId, fetchBookings]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const dt = new Date(dateStr);
    const day = String(dt.getDate()).padStart(2, "0");
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const year = dt.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const isTelemedicineJoinEnabled = (scheduledTime) => {
    if (!scheduledTime) return false;

    const now = new Date();
    const scheduled = new Date(scheduledTime);

    const diffMs = scheduled.getTime() - now.getTime();
    const diffMinutes = diffMs / (1000 * 60);

    return diffMinutes <= 5;
    // return true;
  };
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const dt = new Date(dateStr);
    return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const renderBookingCard = (b) => {
    const paymentClasses =
      b.payment_status === "Paid"
        ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
        : "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300";

    return (
      <div
        key={b.id}
        className="p-3 rounded-xl shadow-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:scale-[1.02] hover:shadow-md transition-all"
      >
        <div className="flex justify-between items-center mb-2 text-xs sm:text-sm">
          <span className="font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1 text-sm sm:text-base">
            <CalendarCheck size={16} /> {t("bookings.table.bookingId")}: {b.id}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${paymentClasses}`}>
            {b.payment_status === "Paid" ? t("bookings.paid") : t("bookings.notPaid")}
          </span>
        </div>

        <div className="flex flex-col gap-1 text-xs sm:text-sm">
          <p className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
            <span className="w-5 h-5 flex items-center justify-center">
              <User size={16} />
            </span>
            <span className="font-medium">{t("bookings.table.doctor")}:</span> {b.doctor?.full_name}
          </p>

          <p className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
            <span className="w-5 h-5 flex items-center justify-center">
              <CalendarCheck size={16} />
            </span>
            <span className="font-medium">{t("bookings.table.slotDate")}:</span> {formatDate(b.slot_time)}
          </p>

          <p className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
            <span className="w-5 h-5 flex items-center justify-center">
              <Clock size={16} />
            </span>
            <span className="font-medium">{t("bookings.table.slotTime")}:</span> {formatTime(b.slot_time)}
          </p>

          <p className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
            <span className="w-5 h-5 flex items-center justify-center">
              <Hash size={16} />
            </span>
            <span className="font-medium">{t("bookings.table.token")}:</span> {b.token_number}
          </p>
          

          {/* TELEMEDICINE CTA */}
          {b.isTelemedicine && b.patient_url && (
            <div className="mt-3 flex justify-end">
              {isTelemedicineJoinEnabled(b.scheduled_time) ? (
                <button
                  onClick={() => window.open(b.patient_url, "_blank")}
                  className="
                    flex items-center gap-2
                    px-3 py-1.5
                    text-xs font-semibold
                    rounded-full
                    bg-purple-600 text-white
                    hover:bg-purple-700
                    transition
                  "
                >
                  <Video size={14} />
                  {t("bookings.table.joinTelemedicine")}
                </button>
              ) : (
                <div
                  className="
                    flex items-center gap-2
                    px-3 py-1.5
                    text-xs font-medium
                    rounded-full
                    bg-gray-200 text-gray-500
                    dark:bg-gray-700 dark:text-gray-400
                    cursor-not-allowed
                  "
                >
                  <Video size={14} />
                  {t("bookings.table.telemedicineAvailableSoon")}
                </div>
              )}
            </div>
          )}


        </div>


      </div>
    );
  };

  const renderBookingsSection = (list, type) => {
    const isUpcoming = type === "upcoming";

    const containerBg = isUpcoming ? "bg-blue-50 dark:bg-blue-900" : "bg-yellow-50 dark:bg-yellow-900";
    const containerBorder = isUpcoming ? "border-blue-200 dark:border-blue-700" : "border-yellow-200 dark:border-yellow-700";
    const sectionTitle = isUpcoming ? t("bookings.upcoming") : t("bookings.past");

    return (
      <div className={`p-4 rounded-2xl mb-6 border ${containerBorder} ${containerBg} shadow-md`}>
        <h4 className="text-lg sm:text-xl font-semibold text-gray-700 dark:text-gray-200 mb-3">{sectionTitle}</h4>
        {list.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map(renderBookingCard)}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center">
            {isUpcoming ? t("bookings.noUpcoming") : t("bookings.noPast")}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      {loading && <Loader message={loadingMessage} />}

      <div className="flex justify-between items-start mb-4">
        {/* Left side: Name + ID */}
        <div className="flex flex-col text-gray-700 dark:text-gray-200">
          {patientName && (
            <span className="font-semibold text-blue-600 dark:text-blue-400 text-base sm:text-lg">
              {patientName}
            </span>
          )}
          {patientIdentifier && (
            <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              ({patientIdentifier})
            </span>
          )}
        </div>

        {/* Right side: Refresh button */}
        <div className="flex justify-end">
          <button
            onClick={fetchBookings}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300"
            title={t("bookings.refresh", "Refresh")}
          >
            <RefreshCcw size={24} className="text-blue-600 dark:text-blue-400" />
          </button>
        </div>
      </div>


      {/* Sections */}
      {renderBookingsSection(bookings.upcoming, "upcoming")}
      {renderBookingsSection(bookings.past, "past")}
    </div>
  );
}
